import { computed, type Ref } from 'vue';
import type { RiskModelInstance } from '@/types/risk-profile';
import type { RiskProfileOptions, RiskProfileCategoryOption } from '@/types/risk-profile-options';
import { useAuthStore } from '@/stores/auth';

/**
 * Composable for shared KYC pricing logic (DRY).
 */
export function useKycPricing(options: Ref<RiskProfileOptions | null>) {
    const authStore = useAuthStore();

    /**
     * Single source of truth for the free entity limit.
     */
    const freeLimit = computed(() => authStore.config?.kyx_free_entities_limit);

    /**
     * Active pricing mode for entities beyond the free limit.
     *   "per_check"  -> each extra entity costs the sum of the enabled check prices.
     *   "per_entity" -> each extra entity costs a flat perEntityPrice.
     */
    const pricingMode = computed(() => authStore.config?.kyx_excess_pricing_mode || 'per_check');

    /**
     * Whether the active mode charges a flat price per extra entity.
     */
    const isPerEntityMode = computed(() => pricingMode.value === 'per_entity');

    /**
     * Flat token cost charged per extra entity when pricingMode === 'per_entity'.
     */
    const perEntityPrice = computed(() => authStore.config?.kyx_per_entity_token_price || 0);

    /**
     * Resolves the token cost charged for each entity beyond the free limit,
     * honouring the active pricing mode. In "per_entity" mode it returns the flat
     * per-entity price; otherwise it falls back to the per-check sum for the instance.
     *
     * @param instance The RiskModelInstance to calculate the per-extra-entity cost for.
     */
    const calculateExcessEntityCost = (instance: RiskModelInstance | null | undefined) => {
        if (isPerEntityMode.value) return perEntityPrice.value;
        return calculateVariableCost(instance);
    };

    /**
     * Calculates the variable cost for a specific risk model instance based on current options.
     * We charge for each unique category slug that has at least one runnable instance.
     * Variants (multiple instances of the same category) are NOT charged individually.
     *
     * @param instance The RiskModelInstance to calculate cost for.
     */
    const calculateVariableCost = (instance: RiskModelInstance | null | undefined) => {
        if (!instance || !options.value) return 0;

        const runnableCategories = new Set<string>();

        // Identify unique categories in the policy
        instance.categories.forEach((cat) => {
            runnableCategories.add(cat.category_slug);
        });

        let total = 0;
        runnableCategories.forEach((slug) => {
            total += getCategoryPrice(slug);
        });

        return total;
    };

    /**
     * Returns the price for a specific category slug.
     */
    const getCategoryPrice = (slug: string) => {
        if (!options.value) return 0;
        const catOption = options.value.categories.find((c: RiskProfileCategoryOption) => c.slug === slug);
        return catOption?.token_price || 0;
    };

    return {
        calculateVariableCost,
        calculateExcessEntityCost,
        getCategoryPrice,
        freeLimit,
        pricingMode,
        isPerEntityMode,
        perEntityPrice
    };
}
